import { InjectionToken, inject } from '@angular/core';
import { ApiHttpClient, injectApiClient } from '@open/ngx-api';
import { apiMapping } from '@assets/api-mapping';
import { BackendClient } from '@assets/api-interfaces';
import { OnCallContracts } from '@assets/api/onCallContracts';

type AdminOnCalls = Pick<OnCallContracts,
  | 'ACCOUNTING_DOCUMENTS-getAgencyAccountingDocuments_onCall'
  | 'ACCOUNTING_DOCUMENTS-previewAccountingDocumentHtml_onCall'
  | 'AGENCIES-getAgenciesByProductContractType_onCall'
  | 'AGENCIES-getAgencyInternalInformation_onCall'
  | 'AGENCIES-updateAgencyInternalInformation_onCall'
  | 'AGENCY_ADMIN-createAgencyAdminProfileDefinition_onCall'
  | 'AGENCY_ADMIN-deleteAgencyAdminProfileDefinition_onCall'
  | 'AGENCY_ADMIN-getAgencyAdminProfileDefinition_onCall'
  | 'AGENCY_ADMIN-getAllAgencyAdminProfileDefinitions_onCall'
  | 'AGENCY_ADMIN-getAllAgencyAdminsWithSettingsInformation_onCall'
  | 'AGENCY_ADMIN-updateAgencyAdminProfileDefinition_onCall'
  | 'AUTH-checkIsActivatedUser_onCall'
  | 'BALANCES-getAgencyContractPaymentBalance_onCall'
  | 'BENEFICIARIES-getAgencyUnonboardedBeneficiaries_onCall'
  | 'BENEFICIARIES-getBeneficiaryEmployeeRecords_onCall'
  | 'BENEFICIARIES-getBeneficiaryQrCodeString_onCall'
  | 'BENEFICIARIES-getBeneficiarySubscription_onCall'
  | 'BENEFICIARIES-getBeneficiarySubscriptionDeactivationJob_onCall'
  | 'BENEFICIARIES-onboardBeneficiaryToAgency_onCall'
  | 'BENEFICIARIES-reactivateBeneficiary_onCall'
  | 'BENEFICIARIES-updateBeneficiaryCommunicationPreferences_onCall'
  | 'BENEFICIARIES-updateBeneficiaryEmployeeRecord_onCall'
  | 'BENEFICIARIES-updateBeneficiaryRestrictedBusinessTags_onCall'
  | 'BENEFICIARIES-updateOrganizationAndSectorization_onCall'
  | 'BILLING-createAgencyCreditNote_onCall'
  | 'BILLING-createAgencyInvoice_onCall'
  | 'BILLING-createBeneficiaryRightsWithdrawal_onCall'
  | 'BILLING-createExternalPaymentToOpen_onCall'
  | 'BILLING-createWalletPayout_onCall'
  | 'BILLING-getSystemRefundWallet_onCall'
  | 'BUSINESSES-getBusinessInternalInformations_onCall'
  | 'BUSINESSES-getContractedAgenciesOfBusiness_onCall'
  | 'BUSINESSES-updateBusinessEligibility_onCall'
  | 'BUSINESSES-updateBusinessInternalInformations_onCall'
  | 'CONTRACTS-createAgencyContract_onCall'
  | 'CONTRACTS-createBeneficiarySubscription_onCall'
  | 'CONTRACTS-createOneClassificationOfAgencyContract_onCall'
  | 'CONTRACTS-getAllClassificationsOfAgencyContract_onCall'
  | 'CONTRACTS-getOneAgencyContract_onCall'
  | 'CONTRACTS-manageAgencyContractTermination_onCall'
  | 'CONTRACTS-removeOneClassificationOfAgencyContract_onCall'
  | 'CONTRACTS-subscribeExistingBeneficiariesToContract_onCall'
  | 'CONTRACTS-updateAgencyContract_onCall'
  | 'CONTRACTS-updateOneClassificationOfAgencyContract_onCall'
  | 'CONTRACTS-updateRestitutionInformationOfAgencyContract_onCall'
  | 'DISCRETE_VOUCHERS-addVoucherRestriction_onCall'
  | 'DISCRETE_VOUCHERS-deleteVoucherRestriction_onCall'
  | 'DISCRETE_VOUCHERS-getBeneficiaryPruneoTransactions_onCall'
  | 'DISCRETE_VOUCHERS-getBeneficiaryVoucherRestriction_onCall'
  | 'INVOICES-getInvoicesZipStoragePath_onCall'
  | 'MERCHANT_NETWORKS-listMerchantNetworks_onCall'
  | 'PAYBACKS-manuallyTriggerProcessCalculateRights_onCall'
  | 'PAYMENTS-createAgencyPaymentBankAccount_onCall'
  | 'PAYMENTS-createAgencyWalletTransfer_onCall'
  | 'PAYMENTS-createWalletTransfer_onCall'
  | 'PAYMENTS-getAgencyPaymentInformation_onCall'
  | 'PAYMENTS-getAllAgencyPaymentBankAccounts_onCall'
  | 'PAYMENTS-getOneAgencyPaymentBankAccount_onCall'
  | 'PAYMENTS-getOneMandateOfAgencyBankAccount_onCall'
  | 'PAYMENTS-manageAgencyPaymentBankAccountDeactivation_onCall'
  | 'PAYMENTS-updateAgencyPaymentBankAccount_onCall'
  | 'PAYMENTS-updateAgencyPaymentSettings_onCall'
  | 'PAYMENT_TRANSACTIONS-getFeesControl_onCall'
  | 'PAYMENT_TRANSACTIONS-getMangopayInvoiceControl_onCall'
  | 'PAYMENT_TRANSACTIONS-processInvoiceReconciliation_onCall'
  | 'PREFECTURE_IMPORT-importPrefectureFile_onCall'
  | 'SECTORS-archiveSector_onCall'
  | 'SECTORS-assignSectorProfile_onCall'
  | 'SECTORS-createGroup_onCall'
  | 'SECTORS-createSectorProfileDefinition_onCall'
  | 'SECTORS-createSectorUser_onCall'
  | 'SECTORS-createSectors_onCall'
  | 'SECTORS-deleteSectorProfileDefinition_onCall'
  | 'SECTORS-getSectorActiveProducts_onCall'
  | 'SECTORS-getSectorPaymentInformation_onCall'
  | 'SECTORS-getSectorProfileDefinitions_onCall'
  | 'SECTORS-getSectorTree_onCall'
  | 'SECTORS-getSectorUsers_onCall'
  | 'SECTORS-getSector_onCall'
  | 'SECTORS-listGroups_onCall'
  | 'SECTORS-moveNode_onCall'
  | 'SECTORS-removeSectorProfileAssignment_onCall'
  | 'SECTORS-updateSectorBillingInfo_onCall'
  | 'SECTORS-updateSectorProfileDefinition_onCall'
  | 'TAGS-createAgencyTagValue_onCall'
  | 'TAGS-createTagFamily_onCall'
  | 'TAGS-deleteAgencyTagValue_onCall'
  | 'TAGS-deleteTagFamily_onCall'
  | 'TAGS-getAgencyTagFamilies_onCall'
  | 'TAGS-listTagFamilies_onCall'
  | 'TAGS-setAgencyTagFamilyActivation_onCall'
  | 'TAGS-updateAgencyTagValue_onCall'
  | 'TAGS-updateTagFamily_onCall'
>;

type AdminRoutes = typeof apiMapping;
// `BackendClient` is an interface: mapping it gives the implicit index signature `HttpRouteContracts` needs.
type AdminRouteContracts = { [RouteName in keyof BackendClient]: BackendClient[RouteName] };

export type AdminApi = ApiHttpClient<AdminOnCalls, AdminRoutes, AdminRouteContracts>;

// Token-backed so specs can `{ provide: ADMIN_API, useValue: stub }`.
export const ADMIN_API: InjectionToken<AdminApi> = new InjectionToken<AdminApi>('ADMIN_API', {
  providedIn: 'root',
  factory: (): AdminApi => injectApiClient<AdminOnCalls, AdminRoutes, AdminRouteContracts>(apiMapping),
});

export function injectApi(): AdminApi {
  return inject(ADMIN_API);
}
